"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Lock, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const features = [{
  id: 1,
  title: "Military-grade encryption"
}, {
  id: 2,
  title: "Secure password vault"
}, {
  id: 3,
  title: "Data breach scanner"
}];
export default function Nordpass() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="nordpasspromo" data-nav="dark" className="relative overflow-hidden bg-[hsl(221,40%,4%)] py-20 text-white lg:py-32">
      {/* Background Decorative Numeral */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-10 -left-10 select-none text-[200px] font-bold text-white/5 md:text-[280px]">
        02
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Heading & Context */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="mb-6 h-16 w-1 bg-gradient-to-b from-primary to-transparent" />
            
            <div className="mb-4 font-mono text-5xl font-semibold tracking-widest text-primary uppercase sm:text-6xl lg:text-7xl">
              02 // NordPass
            </div>

            <h2 className="mb-6 text-4xl font-bold tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Military-grade password security
            </h2>

            <p className="max-w-2xl text-base text-gray-300 sm:text-lg">
              Keep passwords, credit cards, and sensitive notes protected in an
              encrypted vault. Auto-fill credentials and get instant alerts if
              your data appears in a breach.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                <span>Zero-knowledge architecture</span>
              </div>
              <div className="h-1 w-1 rounded-full bg-gray-600" />
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                <span>Cross-device synchronization</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Cyber Card */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <Card className="relative overflow-hidden rounded-lg border border-[hsl(221,30%,15%)] bg-[hsl(221,30%,8%)] p-10 md:p-14 text-white shadow-2xl">
              {/* L-shaped Coordinate Corner Brackets */}
              <div aria-hidden="true" className="pointer-events-none absolute top-0 left-0 h-4 w-4 border-t-2 border-l-2 border-primary" />
              <div aria-hidden="true" className="pointer-events-none absolute right-0 bottom-0 h-4 w-4 border-r-2 border-b-2 border-primary" />

              <CardContent className="p-0">
                {/* Icon Container */}
                <div className="mb-4 flex size-16 md:size-20 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Lock className="size-8 md:size-10" />
                </div>

                {/* Pill Badge */}
                <Badge variant="outline" className="mb-4 rounded-full border-primary/20 bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary">
                  Best for password security
                </Badge>

                {/* Card Title */}
                <h3 className="mb-3 text-3xl md:text-4xl font-bold text-white">
                  Secure Password Vault
                </h3>

                {/* Description */}
                <p className="mb-6 text-base md:text-lg leading-relaxed text-gray-300">
                  Store unlimited passwords, generate secure credentials with one
                  tap, and stay shielded across desktop and mobile devices.
                </p>

                {/* Feature Checklist */}
                <ul className="mb-8 space-y-3">
                  {features.map((item, index) => <li key={item.id} data-index={index} className="flex items-center gap-3 text-base md:text-lg text-gray-200">
                      <CheckCircle2 className="size-5 shrink-0 text-primary" />
                      <span>{item.title}</span>
                    </li>)}
                </ul>

                {/* Card Action */}
                <Button asChild className="w-full rounded-md bg-primary py-4 text-base md:text-lg font-semibold text-primary-foreground shadow-lg transition-[background-color,box-shadow] hover:bg-primary/90">
                  <Link to="https://vpngenix.com/go/Nordpass" newTab className="inline-flex items-center justify-center gap-2">
                    <span>Visit site</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}